import React from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Heart } from "lucide-react";
import { imgSrc, imgSrcSet } from "../lib/imgSrc";

const ProductCard = ({ product, featured = false }) => {
    const isSponsor = product.category === "donation";
    // Sponsorships are intangible — they never go "out of stock".
    // For physical merch, treat stock <= 0 as sold out; 1-4 as low stock.
    const stock = typeof product.stock === "number" ? product.stock : null;
    const outOfStock = !isSponsor && stock !== null && stock <= 0;
    const lowStock = !isSponsor && stock !== null && stock > 0 && stock < 5;
    return (
        <Link
            to={`/product/${product.id}`}
            data-testid={`product-card-${product.id}`}
            aria-label={`${product.name} — $${product.price.toFixed(2)}${outOfStock ? " — sold out" : ""}`}
            aria-disabled={outOfStock}
            className={`group bg-white border border-black/5 rounded-2xl overflow-hidden hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300 flex flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E53935] focus-visible:ring-offset-2 ${
                featured ? "md:col-span-2" : ""
            } ${outOfStock ? "opacity-70" : ""}`}
        >
            <div className="aspect-[4/3] overflow-hidden bg-[#F4F1EA] relative">
                <img
                    src={imgSrc(product.image_url, 640, 72) || undefined}
                    srcSet={imgSrcSet(product.image_url, 640, 72)}
                    alt={product.name}
                    className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${outOfStock ? "grayscale" : ""}`}
                    loading="lazy"
                    decoding="async"
                />
                {outOfStock && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40" data-testid={`product-out-of-stock-overlay-${product.id}`}>
                        <span className="bg-white text-[#1C252E] text-xs font-bold uppercase tracking-[0.18em] px-4 py-2 rounded-full shadow-lg">
                            Sold out
                        </span>
                    </div>
                )}
                {!outOfStock && lowStock && (
                    <span className="absolute top-4 right-4 bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow" data-testid={`product-low-stock-${product.id}`}>
                        Only {stock} left
                    </span>
                )}
                {isSponsor && (
                    <span className="absolute top-4 left-4 bg-[#F2D8D5] text-[#D32F2F] text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                        Sponsor
                    </span>
                )}
                {product.featured && !isSponsor && !outOfStock && (
                    <span className="absolute top-4 left-4 bg-[#1C252E] text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                        Featured
                    </span>
                )}
            </div>
            <div className="p-6 flex flex-col flex-1">
                <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#E53935] mb-2">
                    {isSponsor ? "Sponsorship" : product.category}
                </p>
                <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-2 leading-snug">
                    {product.name}
                </h3>
                <p className="text-sm text-[#4A5568] leading-relaxed line-clamp-2 mb-3 flex-1">
                    {product.description}
                </p>
                {!isSponsor && (
                    <p className="text-[11px] text-[#4A5568] italic mb-3 line-clamp-1" data-testid={`product-fund-note-${product.id}`}>
                        Net proceeds support the Reddog General Support Fund.
                    </p>
                )}
                {isSponsor && (
                    <p className="text-[11px] text-[#4A5568] italic mb-3 line-clamp-1" data-testid={`product-fund-note-${product.id}`}>
                        100% sponsorship — funds the listed program directly.
                    </p>
                )}
                <div className="flex items-center justify-between mt-auto">
                    <p className="font-heading text-2xl text-[#1C252E] font-medium">
                        ${product.price.toFixed(2)}
                    </p>
                    <div
                        className={`flex items-center gap-1.5 text-sm font-medium transition-all ${outOfStock ? "text-slate-400" : "text-[#E53935] group-hover:gap-2.5"}`}
                        data-testid={`product-cta-${product.id}`}
                    >
                        {isSponsor ? (
                            <>
                                <Heart className="w-4 h-4" strokeWidth={1.5} fill="#E53935" />
                                Sponsor
                            </>
                        ) : outOfStock ? (
                            <>Sold out</>
                        ) : (
                            <>
                                <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
                                Shop
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Link>
    );
};

export default ProductCard;
